import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";
import { Overline, Section } from "@/components/shared";
import { PageHero } from "@/components/PageParts";
import { ArrowRight } from "lucide-react";

const CATEGORIES = ["All", "Aging", "Complex Systems", "Mathematical Modeling", "AI and Research", "Business Systems", "Research Notes"];

export default function Articles() {
  useSeo(
    "Research Updates & Articles | Systems Beyond",
    "Articles and research updates on aging, complex systems, mathematical modeling, AI-assisted research and business systems."
  );

  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles").then(({ data }) => {
      setArticles(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = active === "All" ? articles : articles.filter((a) => a.category === active);

  return (
    <div>
      <PageHero
        overline="Articles / Research Updates"
        title="Notes on systems, models and research."
        subtitle="Articles and research updates on aging, complex systems, mathematical modeling, AI-assisted research and business systems."
      />

      <Section className="pb-24 lg:pb-32">
        <div className="flex flex-wrap gap-2 mb-12">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono-sci text-xs px-4 py-2.5 border transition-colors ${
                active === c ? "bg-charcoal text-white border-charcoal" : "border-silver text-dark-gray hover:border-charcoal"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-dark-gray text-sm">Loading articles…</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <Link
                key={a.slug}
                to={`/articles/${a.slug}`}
                data-testid={`article-card-${a.slug}`}
                className="group border border-silver/50 bg-off-white hover:-translate-y-1 hover:border-charcoal transition-all flex flex-col"
              >
                <div className="thumb-illuminate border-b border-silver/50">
                  <img src={a.image} alt={a.title} className="w-full h-48 object-cover" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between font-mono-sci text-[11px] text-blue-gray">
                    <span>{a.category}</span>
                    <span>{a.reading_time}</span>
                  </div>
                  <h3 className="mt-4 text-xl text-ink leading-snug">{a.title}</h3>
                  <p className="mt-3 text-sm text-dark-gray leading-relaxed flex-1">{a.excerpt}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="font-mono-sci text-[11px] text-dark-gray">{a.date}</span>
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink">
                      Read more <ArrowRight size={14} className="text-acid-dark group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-dark-gray text-sm">No articles in this category yet.</p>
        )}
      </Section>
    </div>
  );
}
